import CreateBoard from "@/components/CreateBoard";
import { getBoardsListByIds } from "@/services";
import { useEnableMultiBoard } from "@/utils/hooks/useMultiBoard";
import {
  useKeyPress,
  useMemoizedFn,
  useSetState,
  useUpdateEffect,
} from "ahooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Else, If, Then } from "react-if";
import BoardItem from "./BoardItem";
import styles from "./index.module.less";

// 虚拟列表渲染个数
const RENDER_COUNT = 30;
// item高度
const ITEM_HEIGHT = 50;

const storageBoardCoverMap = {};

interface IProps {
  keyword?: string;
  dataSource: IBoardInfo[];
  onSelectBoard: (boardId: number) => void;
  renderActions?: (board: IBoardInfo) => React.ReactNode;
  hoverShowActions?: boolean;
  hideCreateBoard?: boolean;
  height?: number;
  searchWord?: string;
  boardMatchingResult?: any;
  onUserBoardUpdate?: (board: any) => void;
  enableBoardRecommend?: boolean;
  actionText?: string; // 自定义按钮文本
  actionButtonName?: string; // 自定义按钮的 data-button-name
  sectionTitle?: string; // 自定义区域标题，如"移动至画板"
  buttonTheme?: "black" | "gray"; // 按钮主题
}

export default function BoardList({
  keyword,
  dataSource,
  onSelectBoard,
  boardMatchingResult,
  onUserBoardUpdate,
  searchWord = "",
  renderActions,
  enableBoardRecommend = true,
  hoverShowActions = true,
  hideCreateBoard,
  height,
  actionText,
  actionButtonName,
  sectionTitle,
  buttonTheme,
}: IProps) {
  const [renderIndex, setRenderIndex] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);
  // 添加挂载状态跟踪
  const isMountedRef = useRef(true);

  // 计算顶部精选推荐画板的数量
  const featuredBoardsCount = useMemo(() => {
    // 老版本不显示推荐画板
    if (!enableBoardRecommend || !boardMatchingResult || keyword) return 0;
    const { recommendedBoards, useUserBoardRecommendation } =
      boardMatchingResult;
    if (useUserBoardRecommendation && recommendedBoards?.length > 0) {
      return recommendedBoards.length;
    }
    return 0;
  }, [enableBoardRecommend, boardMatchingResult, keyword]);

  // 计算底部建议的数量
  const relatedBoardsCount = useMemo(() => {
    // 老版本不显示推荐画板
    if (!enableBoardRecommend || !boardMatchingResult || keyword) return 0;
    const { recommendedRelatedBoards, useUserBoardRecommendation } =
      boardMatchingResult;
    if (!useUserBoardRecommendation && recommendedRelatedBoards?.length > 0) {
      return recommendedRelatedBoards.length;
    }
    return 0;
  }, [enableBoardRecommend, boardMatchingResult, keyword]);

  // 计算总的画板数量（精选推荐 + 采集至画板 + 推荐创建）
  const totalBoardsCount =
    featuredBoardsCount + dataSource.length + relatedBoardsCount;

  // 组件卸载时设置挂载状态为false
  useEffect(() => {
    const currentUpdateRef = updateRef.current;
    return () => {
      isMountedRef.current = false;
      // 清理定时器，避免内存泄漏
      if (currentUpdateRef.timer) {
        clearTimeout(currentUpdateRef.timer);
        currentUpdateRef.timer = null;
      }
    };
  }, []);

  const onScroll = useCallback((evt) => {
    const scrollTop = evt.target.scrollTop;
    // 计算渲染起始位置
    const index = Math.floor((scrollTop - 10 * ITEM_HEIGHT) / ITEM_HEIGHT);
    setRenderIndex(index < 0 ? 0 : index);
  }, []);

  // 优化：先获取不带封面的画板，当真实需要渲染时再请求画板展示
  // 带封面画板
  const [coverBoards, setCoverBoards] = useSetState({});
  // 更新封面画板
  const updateRef = useRef({
    timer: null,
    ids: new Set(),
  });
  const updateCovers = useMemoizedFn((id) => {
    // 过滤id不存在的场景
    if (!id) {
      return;
    }

    updateRef.current.ids.add(id);
    if (updateRef.current?.timer) {
      return;
    }

    updateRef.current.timer = setTimeout(async () => {
      const ids = [...updateRef.current?.ids] as number[];
      updateRef.current.ids.clear();
      const { boards, err } = await getBoardsListByIds(ids, 1);
      if (err) {
        return;
      }

      const boardsMap = {};
      boards.forEach((board) => {
        boardsMap[board?.board_id] = board;
        storageBoardCoverMap[board?.board_id] =
          board?.cover?.file || (board?.pins || [])[0]?.file;
      });

      // 检查组件是否仍然挂载，避免在已卸载的组件上更新状态
      if (isMountedRef.current) {
        setCoverBoards(boardsMap);
      }

      updateRef.current.timer = null;
    }, 0);
  });
  const getBoardCover = useMemoizedFn((board) => {
    const boardId = board.board_id;
    if (coverBoards[boardId]) {
      const coverPin =
        coverBoards[boardId]?.cover || (coverBoards[boardId]?.pins || [])[0];
      return coverPin?.file
        ? {
            ...coverPin.file,
            is_private: coverPin?.is_private,
          }
        : null;
    }

    updateCovers(boardId);
    return storageBoardCoverMap[boardId];
  });

  const renderItem = useMemoizedFn((board, index) => {
    const start = renderIndex;
    const end = start + RENDER_COUNT;
    const coverFile = getBoardCover(board);

    if (index < start || index > end) {
      return null;
    }

    // 判断是否是未整理画板：board_id为0或title为"未整理的采集"，且设置了 _icon === "unclassified"
    // 注意：不再使用 isOnlyUnclassified 显示标题，但保留判断逻辑以防其他地方使用
    const isOnlyUnclassified =
      board?.board_id === 0 &&
      (board?.title === "未整理" || board?.title === "未整理的采集") &&
      board?._icon === "unclassified";

    // 调整索引：采集至画板应该从精选推荐画板之后开始
    const adjustedIndex = featuredBoardsCount + index;

    return (
      <BoardItem
        board={board}
        key={board.board_id}
        index={adjustedIndex}
        activeIndex={activeIndex}
        onSelect={onSelectBoard}
        cover={coverFile}
        searchWord={searchWord}
        isOnlyUnclassified={isOnlyUnclassified}
        renderActions={renderActions}
        hoverShowActions={hoverShowActions}
        actionText={actionText}
        actionButtonName={actionButtonName}
        buttonTheme={buttonTheme}
      />
    );
  });

  useKeyPress("enter", async () => {
    // const editing = isEditingElement();
    // if (editing) {
    //   return false;
    // }

    const {
      recommendedBoards,
      recommendedRelatedBoards,
      useUserBoardRecommendation,
    } = boardMatchingResult || {};

    if (activeIndex < featuredBoardsCount) {
      // 区域1：精选推荐画板
      if (recommendedBoards?.[activeIndex]) {
        onSelectBoard(recommendedBoards[activeIndex].board_id);
      }
    } else if (activeIndex < featuredBoardsCount + dataSource.length) {
      // 区域2：采集至画板
      const dataIndex = activeIndex - featuredBoardsCount;
      const board = dataSource[dataIndex];
      if (board) {
        onSelectBoard(board.board_id);
      }
    } else {
      // 区域3：建议
      const relatedIndex =
        activeIndex - featuredBoardsCount - dataSource.length;
      if (recommendedRelatedBoards?.[relatedIndex]) {
        const board = recommendedRelatedBoards[relatedIndex];
        // 处理建议的逻辑
        if (onUserBoardUpdate) {
          const { createBoard } = await import("@/services");
          const createResult = await createBoard({ title: board?.title });
          if (!createResult.err) {
            onUserBoardUpdate(createResult.board);
            onSelectBoard(createResult.board.board_id);
          }
        }
      }
    }
  });

  useKeyPress("uparrow", (evt) => {
    // const editing = isEditingElement();
    // if (editing) {
    //   return false;
    // }

    evt.preventDefault();

    const index = activeIndex <= 0 ? 0 : activeIndex - 1;
    setActiveIndex(index);

    const target = listRef.current.querySelector(`[data-index="${index}"]`);

    target?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  });

  useKeyPress("downarrow", (evt) => {
    // const editing = isEditingElement();
    // if (editing) {
    //   return false;
    // }

    evt.preventDefault();

    const index =
      activeIndex >= totalBoardsCount - 1
        ? totalBoardsCount - 1
        : activeIndex + 1;
    setActiveIndex(index);

    const target = listRef.current.querySelector(`[data-index="${index}"]`);

    target?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  });

  useKeyPress("tab", (evt) => {
    evt.preventDefault();

    if (evt.shiftKey) {
      const index = activeIndex <= 0 ? 0 : activeIndex - 1;
      setActiveIndex(index);

      const target = listRef.current.querySelector(`[data-index="${index}"]`);

      target?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
      return;
    }

    const index =
      activeIndex >= totalBoardsCount - 1
        ? totalBoardsCount - 1
        : activeIndex + 1;
    setActiveIndex(index);

    const target = listRef.current.querySelector(`[data-index="${index}"]`);

    target?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  });

  const onCreate = useCallback(
    (board) => {
      // 如果存在 onUserBoardUpdate，先调用它（用于更新画板列表等）
      if (onUserBoardUpdate) {
        onUserBoardUpdate(board);
      }
      // 然后选择画板
      onSelectBoard(board.board_id);
    },
    [onSelectBoard, onUserBoardUpdate]
  );

  useUpdateEffect(() => {
    keyword && listRef.current?.scrollTo(0, 0);
  }, [keyword]);

  // 渲染精选推荐画板（用户画板数 > 3 时显示在前面）
  const renderFeaturedRecommendations = useCallback(() => {
    if (!boardMatchingResult || keyword) return null;

    const { recommendedBoards, useUserBoardRecommendation } =
      boardMatchingResult;

    if (useUserBoardRecommendation && recommendedBoards.length > 0) {
      return (
        <div className={styles.recommendedSection}>
          <div className={styles.sectionTitle}>推荐采集至</div>
          {recommendedBoards.map((board, index) => (
            <BoardItem
              key={`recommended-${board.board_id}`}
              board={board}
              onSelect={onSelectBoard}
              index={index}
              activeIndex={activeIndex}
              isRecommended={true}
              hoverShowActions={hoverShowActions}
            />
          ))}
        </div>
      );
    }

    return null;
  }, [
    boardMatchingResult,
    keyword,
    onSelectBoard,
    activeIndex,
    hoverShowActions,
  ]);

  // 渲染推荐画板（用户画板数 < 5 或没有画板时显示）
  const renderRecommendedBoards = useCallback(() => {
    if (!boardMatchingResult || keyword) return null;

    const { recommendedRelatedBoards, useUserBoardRecommendation } =
      boardMatchingResult;

    // 在用户画板数 < 5 或没有画板时显示推荐同在画板
    if (!useUserBoardRecommendation && recommendedRelatedBoards.length > 0) {
      return (
        <div className={styles.recommendedSection}>
          <div className={styles.sectionTitle}>建议采集至画板</div>
          {recommendedRelatedBoards.map((board, index) => (
            <BoardItem
              key={`related-${board.board_id}`}
              board={board}
              onSelect={onSelectBoard}
              index={featuredBoardsCount + dataSource.length + index}
              activeIndex={activeIndex}
              isRecommended={true}
              isRelatedBoard={true}
              onUserBoardUpdate={onUserBoardUpdate}
              hoverShowActions={hoverShowActions}
            />
          ))}
        </div>
      );
    }

    return null;
  }, [
    boardMatchingResult,
    keyword,
    onSelectBoard,
    activeIndex,
    onUserBoardUpdate,
    featuredBoardsCount,
    dataSource.length,
    hoverShowActions,
  ]);
  const { enable } = useEnableMultiBoard();

  return (
    <div className={styles.listContainer}>
      <div className={styles.listWrapper}>
        <div
          className={styles.list}
          onScroll={onScroll}
          ref={listRef}
          style={{
            height: height || 300,
          }}
        >
          {enableBoardRecommend && renderFeaturedRecommendations()}
          {sectionTitle && dataSource.length > 0 && !keyword && (
            <div className={styles.sectionTitle}>{sectionTitle}</div>
          )}
          <If condition={dataSource.length > 0}>
            <Then>
              <div
                key={"before"}
                style={{ height: renderIndex * ITEM_HEIGHT }}
              ></div>
              {dataSource.map(renderItem)}
              <div
                key={"after"}
                style={{
                  height:
                    dataSource.length > renderIndex + RENDER_COUNT
                      ? (dataSource.length - (renderIndex + RENDER_COUNT)) *
                        ITEM_HEIGHT
                      : 0,
                }}
              ></div>
            </Then>
            <Else>
              {!keyword && enableBoardRecommend && renderRecommendedBoards()}
            </Else>
          </If>
          {dataSource.length > 0 &&
            enableBoardRecommend &&
            renderRecommendedBoards()}
        </div>
      </div>
      {!hideCreateBoard && (
        <div className={styles.create}>
          <div className={styles.split}></div>
          <CreateBoard
            className={styles.createBoard}
            onCreate={onCreate}
            boardName={keyword}
            createBtnName={
              actionText === "批量移动" ? "创建并移动" : "创建并采集"
            }
            searchWord={searchWord}
            isBlack
          />
        </div>
      )}
    </div>
  );
}
